import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import TimelineRenderer from "@/components/timeline/TimelineRenderer";
import { api, apiErr, mediaUrl, copyText } from "@/lib/api";
import { LAYOUTS, THEMES, sortEvents } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import CommentsThread from "@/components/timeline/CommentsThread";
import ExportDoc from "@/components/timeline/ExportDoc";
import { downloadJSON, exportPNG, exportPDF } from "@/lib/exporter";
import {
  Plus, Copy, Trash2, GripVertical, ChevronLeft, Eye, Share2, Sparkles, Upload,
  Loader2, Check, Type, Palette, Wand2, X, ExternalLink, Play, Code2,
  Users, Download, ChevronDown, MessageSquare, Lock, Trash, History, MousePointer2
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);
const COLORS = ["#3A1F6E", "#6D4AB8", "#9B7BE0", "#C4A7F5", "#4F46E5", "#6366F1", "#0D9488", "#D97706", "#DB2777", "#DC2626"];

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tl, setTl] = useState(null);
  const [selId, setSelId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedSize, setEmbedSize] = useState({ w: "100%", h: "600" });
  const [visibility, setVisibility] = useState("public");
  const [collabOpen, setCollabOpen] = useState(false);
  const [collabs, setCollabs] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "editor" });
  const [inviting, setInviting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [peers, setPeers] = useState([]);
  const cursor = useRef({ x: 0, y: 0 });
  const dragIdx = useRef(null);
  const loaded = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/timelines/${id}`);
        setTl(data);
        setVisibility(data.visibility === "private" ? "public" : data.visibility);
        setSelId(sortEvents(data.events)[0]?.id || null);
        setTimeout(() => { loaded.current = true; }, 300);
      } catch (e) { toast.error(apiErr(e)); nav("/dashboard"); }
    })();
  }, [id, nav]);

  useEffect(() => {
    let alive = true;
    let ws = null;
    let poll = null;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/presence/${id}`;
    const startPolling = () => {
      const beat = async () => {
        try {
          await api.post(`/timelines/${id}/presence`, cursor.current);
          const { data } = await api.get(`/timelines/${id}/presence`);
          if (alive) setPeers(data);
        } catch {}
      };
      beat();
      poll = setInterval(beat, 2500);
    };
    try {
      ws = new WebSocket(wsUrl);
      let sender = null;
      ws.onopen = () => { sender = setInterval(() => { try { ws.readyState === 1 && ws.send(JSON.stringify({ type: "cursor", ...cursor.current })); } catch {} }, 120); };
      ws.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m.type === "peers" && alive) setPeers(m.peers); } catch {} };
      ws.onerror = () => { if (!poll) startPolling(); };
      ws.onclose = () => { if (sender) clearInterval(sender); if (alive && !poll) startPolling(); };
    } catch {
      startPolling();
    }
    return () => { alive = false; if (ws) { try { ws.close(); } catch {} } if (poll) clearInterval(poll); };
  }, [id]);

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      await api.put(`/timelines/${id}`, {
        title: next.title, description: next.description, category: next.category,
        layout: next.layout, theme: next.theme, cover_image: next.cover_image,
        settings: next.settings, events: next.events,
      });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }, [id]);

  const update = (patch) => {
    setTl((prev) => {
      const next = { ...prev, ...patch };
      const canEdit = ["owner", "editor"].includes(next.my_role);
      if (loaded.current && canEdit) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => persist(next), 700);
      }
      return next;
    });
  };

  const updateEvent = (eid, patch) => {
    update({ events: tl.events.map((e) => (e.id === eid ? { ...e, ...patch } : e)) });
  };

  const addEvent = () => {
    const ev = { id: uid(), date: "New date", year: (Math.max(0, ...tl.events.map((e) => Number(e.year) || 0)) + 1), title: "New Event", subtitle: "", description: "", image: "", quote: "", location: "", url: "", source: "", category: "", tags: [], color: COLORS[tl.events.length % COLORS.length] };
    update({ events: [...tl.events, ev] });
    setSelId(ev.id);
  };
  const duplicateEvent = (eid) => {
    const src = tl.events.find((e) => e.id === eid);
    const copy = { ...src, id: uid(), title: `${src.title} (copy)` };
    const i = tl.events.findIndex((e) => e.id === eid);
    const events = [...tl.events]; events.splice(i + 1, 0, copy);
    update({ events }); setSelId(copy.id);
  };
  const deleteEvent = (eid) => {
    const events = tl.events.filter((e) => e.id !== eid);
    update({ events });
    if (selId === eid) setSelId(events[0]?.id || null);
  };

  const onDrop = (idx) => {
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const events = [...tl.events];
    const [moved] = events.splice(dragIdx.current, 1);
    events.splice(idx, 0, moved);
    dragIdx.current = null;
    update({ events });
  };

  const uploadImage = async (file, eid) => {
    const form = new FormData(); form.append("file", file);
    const tId = toast.loading("Uploading…");
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (eid) updateEvent(eid, { image: data.url }); else update({ cover_image: data.url });
      toast.success("Image added", { id: tId });
    } catch (e) { toast.error(apiErr(e), { id: tId }); }
  };

  const copilot = async (action, eid) => {
    const ev = tl.events.find((e) => e.id === eid);
    const text = action === "improve_title" ? ev.title : ev.description;
    if (!text) { toast.error("Add some text first"); return; }
    const tId = toast.loading("AI is writing…");
    try {
      const { data } = await api.post("/ai/copilot", { action, text });
      if (action === "improve_title") updateEvent(eid, { title: data.result }); else updateEvent(eid, { description: data.result });
      toast.success("Updated", { id: tId });
    } catch (e) { toast.error(apiErr(e), { id: tId }); }
  };

  const autoOrder = async () => {
    if (tl.events.length < 2) { toast.error("Add at least two events first"); return; }
    const tId = toast.loading("AI is ordering your events…");
    try {
      const payload = tl.events.map((e) => ({ id: e.id, date: e.date || "", title: e.title || "" }));
      const { data } = await api.post("/ai/organize", { events: payload });
      const years = data.years || {};
      const events = tl.events.map((e) => (years[e.id] !== undefined ? { ...e, year: years[e.id] } : e));
      update({ events });
      toast.success("Events ordered chronologically", { id: tId });
    } catch (e) { toast.error(apiErr(e), { id: tId }); }
  };

  const isSparse = (v) => !v || String(v).trim() === "" || String(v).trim().toLowerCase() === "new date" || String(v).trim() === "New Event";

  const enrich = async () => {
    if (tl.events.length === 0) { toast.error("Add an event first"); return; }
    const tId = toast.loading("AI is filling in dates & summaries…");
    try {
      const payload = tl.events.map((e) => ({ id: e.id, title: e.title || "", date: e.date || "", description: e.description || "" }));
      const { data } = await api.post("/ai/enrich", { topic: tl.title || "", events: payload });
      const patches = data.events || {};
      let filled = 0;
      const events = tl.events.map((e) => {
        const p = patches[e.id];
        if (!p) return e;
        const next = { ...e };
        if (p.date && isSparse(e.date)) { next.date = p.date; filled++; }
        if (p.year !== undefined && (e.year === "" || e.year === undefined || e.year === null)) next.year = p.year;
        if (p.description && (!e.description || e.description.trim().length < 40)) { next.description = p.description; filled++; }
        return next;
      });
      update({ events });
      toast.success(filled > 0 ? "Sparse events enriched" : "Everything already looks complete", { id: tId });
    } catch (e) { toast.error(apiErr(e), { id: tId }); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/timelines/${id}/publish`, { visibility });
      setTl((p) => ({ ...p, ...data }));
      toast.success("Published");
      setPublishOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setPublishing(false); }
  };

  const openCollab = async () => {
    setCollabOpen(true);
    try { const { data } = await api.get(`/timelines/${id}/collaborators`); setCollabs(data); } catch (e) { toast.error(apiErr(e)); }
  };
  const addCollab = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post(`/timelines/${id}/collaborators`, invite);
      const { data: full } = await api.get(`/timelines/${id}/collaborators`);
      setCollabs(full); setInvite({ email: "", role: "editor" });
      toast.success("Collaborator added");
    } catch (err) { toast.error(apiErr(err)); } finally { setInviting(false); }
  };
  const removeCollab = async (cid) => {
    try { await api.delete(`/timelines/${id}/collaborators/${cid}`); setCollabs((p) => p.filter((c) => c.user_id !== cid)); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const runExport = async (kind) => {
    setExporting(true);
    const tId = toast.loading(`Exporting ${kind.toUpperCase()}…`);
    try {
      if (kind === "json") downloadJSON(tl);
      else if (kind === "png") await exportPNG(exportRef.current, tl.title);
      else if (kind === "pdf") await exportPDF(exportRef.current, tl.title);
      toast.success(`${kind.toUpperCase()} downloaded`, { id: tId });
    } catch (e) { toast.error("Export failed. Try again.", { id: tId }); }
    finally { setExporting(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try { const { data } = await api.get(`/timelines/${id}/versions`); setVersions(data); } catch (e) { toast.error(apiErr(e)); }
  };
  const saveVersion = async () => {
    try {
      await api.post(`/timelines/${id}/versions`, { label: `Snapshot · ${new Date().toLocaleString()}` });
      const { data } = await api.get(`/timelines/${id}/versions`); setVersions(data);
      toast.success("Snapshot saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const restoreVersion = async (vid) => {
    try {
      const { data } = await api.post(`/timelines/${id}/versions/${vid}/restore`);
      loaded.current = false;
      setTl((p) => ({ ...p, ...data }));
      setSelId(sortEvents(data.events)[0]?.id || null);
      setTimeout(() => { loaded.current = true; }, 300);
      const { data: vs } = await api.get(`/timelines/${id}/versions`); setVersions(vs);
      toast.success("Version restored");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!tl) return <div className="min-h-screen bg-background p-6"><Skeleton className="h-16 w-full mb-4" /><div className="grid grid-cols-12 gap-4"><Skeleton className="col-span-3 h-[70vh]" /><Skeleton className="col-span-6 h-[70vh]" /><Skeleton className="col-span-3 h-[70vh]" /></div></div>;

  const sel = tl.events.find((e) => e.id === selId);
  const ordered = sortEvents(tl.events);
  const isOwner = tl.is_owner !== false && (tl.my_role === "owner" || tl.is_owner === true);
  const canEdit = ["owner", "editor"].includes(tl.my_role);
  const publicUrl = `${window.location.origin}/t/${tl.creator_username}/${tl.slug}`;
  const presentUrl = `${window.location.origin}/present/${tl.creator_username}/${tl.slug}?tid=${tl.id}`;
  const embedUrl = `${window.location.origin}/embed/${tl.creator_username}/${tl.slug}`;
  const embedCode = `<iframe src="${embedUrl}" width="${embedSize.w}" height="${embedSize.h}" style="border:0;border-radius:16px;overflow:hidden" title="${tl.title}" loading="lazy"></iframe>`;

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Top bar */}
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/dashboard"><Button variant="ghost" size="icon" data-testid="editor-back"><ChevronLeft size={20} /></Button></Link>
          <Logo showWord={false} />
          <Input value={tl.title} onChange={(e) => update({ title: e.target.value })} data-testid="editor-title"
            className="h-9 border-0 bg-transparent font-display font-semibold text-base focus-visible:ring-1 max-w-[280px]" />
          <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
            {saving ? <><Loader2 size={12} className="animate-spin" /> Saving</> : <><Check size={12} /> Saved</>}
          </span>
          {peers.length > 0 && (
            <div className="flex items-center -space-x-2 ml-1" data-testid="presence-avatars">
              {peers.slice(0, 4).map((p) => (
                <span key={p.user_id} title={p.name} className="h-7 w-7 rounded-full grid place-items-center text-[11px] font-semibold text-white ring-2 ring-background" style={{ background: p.color }}>
                  {(p.name || "U").slice(0, 1).toUpperCase()}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!canEdit && <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground flex items-center gap-1" data-testid="role-badge"><Lock size={11} /> {tl.my_role} · view only</span>}
          {isOwner && <Button variant="ghost" size="sm" onClick={openCollab} data-testid="editor-share-people-btn"><Users size={15} className="mr-1" /> Share</Button>}
          {canEdit && <Button variant="ghost" size="sm" onClick={openHistory} data-testid="editor-history-btn"><History size={15} className="mr-1" /> History</Button>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" disabled={exporting} data-testid="editor-export-btn"><Download size={15} className="mr-1" /> Export <ChevronDown size={14} className="ml-0.5" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => runExport("pdf")} data-testid="export-pdf">PDF document</DropdownMenuItem>
              <DropdownMenuItem onClick={() => runExport("png")} data-testid="export-png">PNG image</DropdownMenuItem>
              <DropdownMenuItem onClick={() => runExport("json")} data-testid="export-json">JSON data</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <a href={presentUrl} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm" data-testid="editor-present-btn"><Play size={15} className="mr-1" /> Present</Button></a>
          <Button variant="ghost" size="sm" onClick={() => setEmbedOpen(true)} data-testid="editor-embed-btn"><Code2 size={15} className="mr-1" /> Embed</Button>
          {tl.status === "published" && <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm" data-testid="editor-view-public"><Eye size={16} className="mr-1" /> View</Button></a>}
          {isOwner && <Button onClick={() => setPublishOpen(true)} size="sm" className="rounded-full" data-testid="editor-publish-btn"><Share2 size={15} className="mr-1" /> {tl.status === "published" ? "Published" : "Publish"}</Button>}
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Left: structure */}
        <aside className="w-64 shrink-0 border-r border-border flex flex-col">
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Timeline</span>
            <div className="flex items-center gap-1">
              {canEdit && <button onClick={enrich} data-testid="ai-enrich-btn" title="Auto-fill dates & summaries with AI" className="h-7 w-7 grid place-items-center rounded-lg hover:bg-muted text-primary"><Wand2 size={15} /></button>}
              {canEdit && <button onClick={autoOrder} data-testid="auto-order-btn" title="Auto-order chronologically with AI" className="h-7 w-7 grid place-items-center rounded-lg hover:bg-muted text-primary"><Sparkles size={15} /></button>}
              <button onClick={addEvent} data-testid="add-event-btn" className="h-7 w-7 grid place-items-center rounded-lg hover:bg-muted text-primary"><Plus size={16} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
            {ordered.map((ev, i) => (
              <div key={ev.id} draggable onDragStart={() => (dragIdx.current = tl.events.findIndex((e) => e.id === ev.id))}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(tl.events.findIndex((e) => e.id === ev.id))}
                onClick={() => setSelId(ev.id)} data-testid={`event-item-${i}`}
                className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${selId === ev.id ? "bg-accent" : "hover:bg-muted"}`}>
                <GripVertical size={14} className="text-muted-foreground/50 cursor-grab shrink-0" />
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: ev.color }} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium truncate ${selId === ev.id ? "text-primary" : "text-foreground"}`}>{ev.title || "Untitled"}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{ev.date}</div>
                </div>
                <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); duplicateEvent(ev.id); }} className="h-6 w-6 grid place-items-center rounded hover:bg-background text-muted-foreground"><Copy size={13} /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteEvent(ev.id); }} className="h-6 w-6 grid place-items-center rounded hover:bg-background text-destructive"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
            <button onClick={addEvent} className="w-full mt-2 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <Plus size={15} /> Add event
            </button>
          </div>
        </aside>

        {/* Center: live preview */}
        <main className="flex-1 min-w-0 overflow-y-auto bg-muted/30" onMouseMove={(e) => { cursor.current = { x: +(e.clientX / window.innerWidth).toFixed(4), y: +(e.clientY / window.innerHeight).toFixed(4) }; }}>
          <TimelineRenderer timeline={tl} />
        </main>

        {/* Right: inspector */}
        <aside className="w-80 shrink-0 border-l border-border flex flex-col">
          <Tabs defaultValue="content" className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-3 mt-3 grid grid-cols-3">
              <TabsTrigger value="content" data-testid="tab-content"><Type size={14} className="mr-1" /> Content</TabsTrigger>
              <TabsTrigger value="design" data-testid="tab-design"><Palette size={14} className="mr-1" /> Design</TabsTrigger>
              <TabsTrigger value="animation" data-testid="tab-animation"><Wand2 size={14} className="mr-1" /> Motion</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-4">
              <TabsContent value="content" className="mt-0 space-y-4">
                {!sel ? <p className="text-sm text-muted-foreground">Select an event to edit its content.</p> : (
                  <>
                    <Field label="Date (display)"><Input value={sel.date} onChange={(e) => updateEvent(sel.id, { date: e.target.value })} placeholder="1969 or c. 3000 BCE" data-testid="ev-date" /></Field>
                    <Field label="Sort year (number)"><Input type="number" value={sel.year} onChange={(e) => updateEvent(sel.id, { year: e.target.value })} placeholder="1969 (negative for BCE)" data-testid="ev-year" /></Field>
                    <Field label="Title">
                      <div className="flex gap-1.5">
                        <Input value={sel.title} onChange={(e) => updateEvent(sel.id, { title: e.target.value })} data-testid="ev-title" />
                        <Button variant="outline" size="icon" onClick={() => copilot("improve_title", sel.id)} title="Improve with AI"><Sparkles size={14} /></Button>
                      </div>
                    </Field>
                    <Field label="Subtitle"><Input value={sel.subtitle} onChange={(e) => updateEvent(sel.id, { subtitle: e.target.value })} data-testid="ev-subtitle" /></Field>
                    <Field label="Description">
                      <Textarea value={sel.description} onChange={(e) => updateEvent(sel.id, { description: e.target.value })} className="min-h-[100px]" data-testid="ev-description" />
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <AiChip onClick={() => copilot("expand", sel.id)}>Expand</AiChip>
                        <AiChip onClick={() => copilot("shorten", sel.id)}>Shorten</AiChip>
                        <AiChip onClick={() => copilot("rewrite", sel.id)}>Rewrite</AiChip>
                        <AiChip onClick={() => copilot("educational", sel.id)}>Educational</AiChip>
                      </div>
                    </Field>
                    <Field label="Image">
                      {sel.image && <div className="mb-2 rounded-lg overflow-hidden border border-border relative group"><img src={mediaUrl(sel.image)} alt="" className="w-full h-28 object-cover" /><button onClick={() => updateEvent(sel.id, { image: "" })} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded bg-background/80"><X size={14} /></button></div>}
                      <label className="flex items-center justify-center gap-2 h-10 rounded-lg border border-dashed border-border cursor-pointer text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
                        <Upload size={15} /> Upload image
                        <input type="file" accept="image/*" className="hidden" data-testid="ev-image-upload" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], sel.id)} />
                      </label>
                    </Field>
                    <Field label="Quote"><Textarea value={sel.quote} onChange={(e) => updateEvent(sel.id, { quote: e.target.value })} className="min-h-[60px]" data-testid="ev-quote" /></Field>
                    <Field label="Location"><Input value={sel.location} onChange={(e) => updateEvent(sel.id, { location: e.target.value })} data-testid="ev-location" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Latitude"><Input type="number" value={sel.lat ?? ""} onChange={(e) => updateEvent(sel.id, { lat: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="e.g. 48.8566" data-testid="ev-lat" /></Field>
                      <Field label="Longitude"><Input type="number" value={sel.lng ?? ""} onChange={(e) => updateEvent(sel.id, { lng: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="e.g. 2.3522" data-testid="ev-lng" /></Field>
                    </div>
                    <Field label="Tags (comma separated)"><Input value={(sel.tags || []).join(", ")} onChange={(e) => updateEvent(sel.id, { tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="ev-tags" /></Field>
                    <Field label="Branch (for Branches layout)"><Input value={sel.branch || ""} onChange={(e) => updateEvent(sel.id, { branch: e.target.value })} placeholder="e.g. Main, Alternate path" data-testid="ev-branch" /></Field>
                    <Field label="Marker color">
                      <div className="flex flex-wrap gap-2">
                        {COLORS.map((c) => <button key={c} onClick={() => updateEvent(sel.id, { color: c })} className={`h-7 w-7 rounded-full transition-transform ${sel.color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`} style={{ background: c }} />)}
                      </div>
                    </Field>
                    <div className="pt-4 mt-2 border-t border-border">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1"><MessageSquare size={13} /> Discussion</div>
                      <CommentsThread timelineId={tl.id} eventId={sel.id} canComment={["owner", "editor", "commenter"].includes(tl.my_role)} />
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="design" className="mt-0 space-y-5">
                <Field label="Layout">
                  <div className="grid grid-cols-1 gap-2">
                    {LAYOUTS.map((l) => (
                      <button key={l.id} onClick={() => update({ layout: l.id })} data-testid={`set-layout-${l.id}`}
                        className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${tl.layout === l.id ? "border-primary bg-accent text-primary" : "border-border hover:bg-muted"}`}>
                        <div className="font-medium">{l.name}</div>
                        <div className="text-xs text-muted-foreground">{l.desc}</div>
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Theme">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(THEMES).map(([key, th]) => (
                      <button key={key} onClick={() => update({ theme: key })} data-testid={`set-theme-${key}`}
                        className={`rounded-lg border overflow-hidden transition-transform hover:-translate-y-0.5 ${tl.theme === key ? "ring-2 ring-primary" : "border-border"}`}>
                        <div className="h-12" style={{ background: th.bgGradient }} />
                        <div className="px-2 py-1.5 text-xs font-medium text-left">{th.name}</div>
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Description"><Textarea value={tl.description} onChange={(e) => update({ description: e.target.value })} className="min-h-[70px]" data-testid="tl-description" /></Field>
                <Field label="Cover image">
                  {tl.cover_image && <div className="mb-2 rounded-lg overflow-hidden border border-border"><img src={mediaUrl(tl.cover_image)} alt="" className="w-full h-24 object-cover" /></div>}
                  <label className="flex items-center justify-center gap-2 h-10 rounded-lg border border-dashed border-border cursor-pointer text-sm text-muted-foreground hover:border-primary/40 transition-colors">
                    <Upload size={15} /> Upload cover
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], null)} />
                  </label>
                </Field>
              </TabsContent>

              <TabsContent value="animation" className="mt-0 space-y-5">
                <p className="text-sm text-muted-foreground">Published timelines animate as viewers scroll. The chronological spine draws itself into existence and events fade up in sequence.</p>
                <ToggleRow label="Entrance animations" checked={tl.settings?.animate !== false} onChange={(v) => update({ settings: { ...tl.settings, animate: v } })} />
                <ToggleRow label="Show time navigator" checked={tl.settings?.show_navigator !== false} onChange={(v) => update({ settings: { ...tl.settings, show_navigator: v } })} />
                <ToggleRow label='"Made with Timeline" badge' checked={tl.settings?.show_branding !== false} onChange={(v) => update({ settings: { ...tl.settings, show_branding: v } })} />
                <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">Motion respects <b>prefers-reduced-motion</b> for accessibility.</div>
              </TabsContent>
            </div>
          </Tabs>
        </aside>
      </div>

      {/* Publish dialog */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Publish timeline</DialogTitle>
            <DialogDescription>Give your timeline a home on the web with a clean, shareable URL.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Visibility">
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger data-testid="visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public — anyone with the link, listed in Discover</SelectItem>
                  <SelectItem value="unlisted">Unlisted — anyone with the link</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {tl.status === "published" && (
              <div className="flex items-center gap-2 rounded-lg border border-border p-2.5">
                <input readOnly value={publicUrl} className="flex-1 bg-transparent text-sm text-muted-foreground outline-none" />
                <Button size="sm" variant="outline" onClick={() => { copyText(publicUrl); toast.success("Link copied"); }}>Copy</Button>
                <a href={publicUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink size={15} /></Button></a>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={publish} disabled={publishing} className="rounded-full" data-testid="confirm-publish-btn">
              {publishing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Share2 size={15} className="mr-1.5" />}
              {tl.status === "published" ? "Update" : "Publish now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed dialog */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent data-testid="embed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Embed this timeline</DialogTitle>
            <DialogDescription>Drop it into any website, blog, or documentation with one line of HTML.</DialogDescription>
          </DialogHeader>
          {tl.status !== "published" ? (
            <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
              Publish your timeline first to enable embedding. The embed shows the public version.
              <div className="mt-3"><Button size="sm" className="rounded-full" onClick={() => { setEmbedOpen(false); setPublishOpen(true); }}>Publish now</Button></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Width"><Input value={embedSize.w} onChange={(e) => setEmbedSize((s) => ({ ...s, w: e.target.value }))} data-testid="embed-width" /></Field>
                <Field label="Height (px)"><Input value={embedSize.h} onChange={(e) => setEmbedSize((s) => ({ ...s, h: e.target.value }))} data-testid="embed-height" /></Field>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Embed code</Label>
                <Textarea readOnly value={embedCode} className="font-mono text-xs min-h-[90px]" data-testid="embed-code" onFocus={(e) => e.target.select()} />
              </div>
              <div className="flex items-center gap-2">
                <Button className="rounded-full" onClick={() => { copyText(embedCode); toast.success("Embed code copied"); }} data-testid="copy-embed-btn"><Code2 size={15} className="mr-1.5" /> Copy code</Button>
                <a href={embedUrl} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink size={15} className="mr-1" /> Preview</Button></a>
              </div>
              <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">The embed auto-resizes to fit its content and stays responsive on mobile.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Collaborators dialog */}
      <Dialog open={collabOpen} onOpenChange={setCollabOpen}>
        <DialogContent data-testid="collab-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Share & collaborate</DialogTitle>
            <DialogDescription>Invite people by email to view, comment on, or edit this timeline together.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addCollab} className="flex items-end gap-2">
            <div className="flex-1"><Label className="text-xs text-muted-foreground mb-1.5 block">Email</Label>
              <Input type="email" required value={invite.email} onChange={(e) => setInvite((s) => ({ ...s, email: e.target.value }))} placeholder="teammate@example.com" data-testid="collab-email" /></div>
            <Select value={invite.role} onValueChange={(v) => setInvite((s) => ({ ...s, role: v }))}>
              <SelectTrigger className="w-32" data-testid="collab-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="commenter">Commenter</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={inviting} className="rounded-full" data-testid="collab-invite-btn">{inviting ? <Loader2 className="animate-spin" size={16} /> : "Invite"}</Button>
          </form>
          <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
            {collabs.map((c) => (
              <div key={c.user_id} className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid={`collab-row-${c.user_id}`}>
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs">{(c.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                </div>
                <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{c.role}</span>
                {c.role !== "owner" && <button onClick={() => removeCollab(c.user_id)} className="text-muted-foreground hover:text-destructive" data-testid={`collab-remove-${c.user_id}`}><Trash size={15} /></button>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ExportDoc ref={exportRef} timeline={tl} />

      {/* Live cursors */}
      <div className="fixed inset-0 pointer-events-none z-[60]" data-testid="live-cursors">
        {peers.map((p) => (
          <div key={p.user_id} className="absolute transition-all duration-300 ease-out" style={{ left: `${p.x * 100}vw`, top: `${p.y * 100}vh` }}>
            <MousePointer2 size={18} style={{ color: p.color, fill: p.color }} />
            <span className="ml-3 -mt-1 inline-block text-[10px] font-medium text-white px-1.5 py-0.5 rounded" style={{ background: p.color }}>{p.name}</span>
          </div>
        ))}
      </div>

      {/* Version history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Version history</DialogTitle>
            <DialogDescription>Save snapshots as you work, and restore any earlier version.</DialogDescription>
          </DialogHeader>
          <Button onClick={saveVersion} className="rounded-full w-fit" data-testid="save-version-btn"><History size={15} className="mr-1.5" /> Save current snapshot</Button>
          <div className="mt-2 space-y-2 max-h-72 overflow-y-auto">
            {versions.length === 0 && <p className="text-sm text-muted-foreground">No snapshots yet. Save one to start tracking history.</p>}
            {versions.map((v) => (
              <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`version-${v.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{v.label}</div>
                  <div className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleString()} · {v.event_count} events · {v.created_by_name}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => restoreVersion(v.id)} data-testid={`restore-${v.id}`}>Restore</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>{children}</div>
);
const AiChip = ({ children, onClick }) => (
  <button onClick={onClick} className="text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors inline-flex items-center gap-1"><Sparkles size={10} /> {children}</button>
);
const ToggleRow = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between"><span className="text-sm text-foreground">{label}</span><Switch checked={checked} onCheckedChange={onChange} /></div>
);
